import {Filter} from '@github-ui/filter'
import {hpcAttrs} from '@github-ui/hpc'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQuery} from '@github-ui/react-query'
import {SoftNavOnlyLink as Link, useParams} from '@github-ui/react-router'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {Heading} from '@primer/react'
import {clsx} from 'clsx'
import {type MouseEventHandler, useCallback, useEffect, useRef, useState} from 'react'

import {LABELS} from '../../constants'
import {useSavedViewEndpoints} from '../../contexts/SavedViewEndpointsContext'
import {useCreateSavedViewMutation} from '../../hooks/use-create-saved-view-mutation'
import {useSavedViewAnalytics} from '../../hooks/use-saved-view-analytics'
import type {UseSavedViewEditorReturn} from '../../hooks/use-saved-view-editor'
import {useSavedViewFilterProviders} from '../../hooks/use-saved-view-filter-providers'
import {savedViewsQueryOptions} from '../../queries'
import {SAVED_VIEW_OPEN_SOURCES, type SavedViewOpenLocationState} from '../../saved-view-open-source'
import type {SavedViewIssuesRenderer, SavedViewsResponse, SavedViewVisibility} from '../../types'
import {viewVisibilityFromParam} from '../../utils/views-visibility'
import {SavedViewMutationErrorBanner} from '../dialogs/SavedViewMutationErrorBanner'
import {ShortcutEditingHeader} from '../ShortcutEditingHeader'
import styles from './RepositoryViewNewPage.module.css'
import {RepositoryViewNewPreview} from './RepositoryViewNewPreview'
import {VisibilitySelect} from './VisibilitySelect'

interface RepositoryViewNewContentProps {
  // Default to the repository in the route. Account level surfaces are not under
  // a repository, so they pass their own pair.
  owner?: string
  repo?: string
  editor: UseSavedViewEditorReturn
  confirmDiscardIfDirty: () => Promise<boolean>
  onBackLinkClick?: MouseEventHandler<HTMLAnchorElement>
  IssuesRenderer?: SavedViewIssuesRenderer
  visibilityEnabled?: boolean
  sidebarLayoutEnabled?: boolean
  initialData?: SavedViewsResponse
}

export function RepositoryViewNewContent({
  owner: ownerProp,
  repo: repoProp,
  editor,
  confirmDiscardIfDirty,
  onBackLinkClick,
  IssuesRenderer,
  visibilityEnabled: visibilityEnabledProp,
  sidebarLayoutEnabled: sidebarLayoutEnabledProp,
  initialData,
}: RepositoryViewNewContentProps) {
  const {owner: routeOwner = '', repo: routeRepo = ''} = useParams<{owner: string; repo: string}>()
  const owner = ownerProp ?? routeOwner
  const repo = repoProp ?? routeRepo
  const navigate = useNavigate()
  const repositorySidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const sidebarLayoutEnabled = sidebarLayoutEnabledProp ?? repositorySidebarLayoutEnabled

  const {basePath: viewsPath, listUrl} = useSavedViewEndpoints(owner, repo)

  const {data: savedViewsData} = useQuery(savedViewsQueryOptions({owner, repo, listUrl, initialData}))
  const viewerCanManageViews = savedViewsData?.viewerCanManageViews
  useEffect(() => {
    if (viewerCanManageViews === false) {
      navigate(viewsPath, {replace: true})
    }
  }, [navigate, viewerCanManageViews, viewsPath])

  const {trackNewVisited} = useSavedViewAnalytics()
  const {shortcut, setShortcutValue, previewQuery, commitPreviewQuery, previewQueryContextValue} = editor

  const filterProviders = useSavedViewFilterProviders({owner, repo})

  const {mutate, isPending, error} = useCreateSavedViewMutation({owner, repo})

  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views_team_vs_personal uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const repositoryVisibilityEnabled = useFeatureFlag('issue_pinned_views_team_vs_personal')
  const visibilityEnabled = visibilityEnabledProp ?? repositoryVisibilityEnabled
  const [searchParams] = useSearchParams()
  const visibilityParam = searchParams.get('visibility')
  const [visibility, setVisibility] = useState<SavedViewVisibility>(
    () => viewVisibilityFromParam(visibilityParam) ?? 'personal',
  )
  const hasTrackedVisitRef = useRef(false)
  useEffect(() => {
    if (hasTrackedVisitRef.current) return
    hasTrackedVisitRef.current = true
    trackNewVisited(visibility)
  }, [trackNewVisited, visibility])

  const handleDismiss = useCallback(async () => {
    if (await confirmDiscardIfDirty()) {
      navigate(viewsPath)
    }
  }, [confirmDiscardIfDirty, navigate, viewsPath])

  const handleSave = useCallback(() => {
    mutate(
      {
        name: shortcut.name.trim(),
        query: shortcut.query.trim(),
        description: shortcut.description?.trim() || undefined,
        icon: shortcut.icon,
        color: shortcut.color,
        source: 'new',
        visibility: visibilityEnabled ? visibility : undefined,
      },
      {
        onSuccess: ({savedView}) => {
          const openState: SavedViewOpenLocationState = {savedViewOpenSource: SAVED_VIEW_OPEN_SOURCES.created}
          navigate(`${viewsPath}/${encodeURIComponent(savedView.id)}`, {state: openState})
        },
      },
    )
  }, [mutate, navigate, viewsPath, shortcut, visibility, visibilityEnabled])

  if (viewerCanManageViews === false) {
    return null
  }

  return (
    <div className={clsx(!sidebarLayoutEnabled && styles.middlePaneWrapper)} {...hpcAttrs()}>
      {!sidebarLayoutEnabled && (
        <>
          <Link to={viewsPath} className={styles.backLink} onClick={onBackLinkClick}>
            <ArrowLeftIcon />
            {LABELS.views.backToViews}
          </Link>
          <Heading as="h1" variant="medium" className={styles.heading}>
            {LABELS.views.createLink}
          </Heading>
        </>
      )}
      <div className={styles.middlePaneGrid}>
        <SavedViewMutationErrorBanner
          message={error ? LABELS.views.createError : null}
          onRetry={handleSave}
          isRetrying={isPending}
        />
        <ShortcutEditingHeader
          shortcut={shortcut}
          setShortcutValue={setShortcutValue}
          onSave={handleSave}
          onDismiss={handleDismiss}
          isSaving={isPending}
          helperText={
            visibilityEnabled
              ? visibility === 'personal'
                ? LABELS.views.createDescriptionPersonal
                : LABELS.views.createDescriptionShared
              : LABELS.views.createDescriptionTeam
          }
          trailingField={
            visibilityEnabled ? <VisibilitySelect value={visibility} onChange={setVisibility} /> : undefined
          }
          renderFilter={({inputRef, className}) => (
            <Filter
              id="repo-view-new-filter"
              className={className}
              inputRef={inputRef}
              label={LABELS.views.filterQueryLabel}
              placeholder={LABELS.views.filterQueryPlaceholder}
              variant="input"
              providers={filterProviders}
              filterValue={shortcut.query}
              onChange={value => setShortcutValue('query', value)}
              onSubmit={request => commitPreviewQuery(request.raw)}
              settings={{aliasMatching: true, groupAndKeywordSupport: true}}
            />
          )}
        />
        {IssuesRenderer ? (
          <RepositoryViewNewPreview
            owner={owner}
            repo={repo}
            query={previewQuery}
            previewQueryContextValue={previewQueryContextValue}
            IssuesRenderer={IssuesRenderer}
          />
        ) : null}
      </div>
    </div>
  )
}
